import { showToast } from "../../utils/toast";
import { sendJson } from "./request";
import { enhanceFormControls } from "./form_controls";

export interface TaskPrefill {
  id?: string;
  title?: string;
  task_type?: string;
  priority?: string;
  due_date?: string;
  due_time?: string;
  reminder?: string | number | null;
  assigned_to?: string;
  notes?: string;
  customer_id?: string;
}

const modal = (): HTMLElement | null => document.querySelector<HTMLElement>("[data-cs-task-modal]");

function setType(form: HTMLFormElement, type: string): void {
  form.querySelector<HTMLInputElement>("[data-cs-task-type]")!.value = type;
  form.querySelectorAll<HTMLElement>("[data-cs-task-type-value]").forEach((button) => button.classList.toggle("active", button.dataset.csTaskTypeValue === type));
}

function setField(form: HTMLFormElement, name: string, value: unknown): void {
  const field = form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
  if (!field || value === undefined || value === null) return;
  field.value = String(value);
  field.dispatchEvent(new Event("cs:sync"));
}

// Opens the modal empty (new task), with a title (e.g. "Call Anna"), or filled from a task (edit).
export function openTaskModal(prefill: TaskPrefill = {}): void {
  const box = modal();
  const form = box?.querySelector<HTMLFormElement>("[data-cs-task-form]");
  if (!box || !form) return;

  enhanceFormControls(box);
  form.reset();
  setType(form, prefill.task_type || "TODO");
  const editing = Boolean(prefill.id);
  form.querySelector<HTMLInputElement>("[data-cs-task-id]")!.value = prefill.id || "";
  ["title", "priority", "due_date", "due_time", "reminder", "assigned_to", "notes", "customer_id"].forEach((name) => setField(form, name, (prefill as any)[name]));
  form.querySelectorAll<HTMLInputElement>("input[data-cs-date]").forEach((input) => input.dispatchEvent(new Event("cs:sync")));
  box.querySelector("[data-cs-task-heading]")!.textContent = editing ? "Edit task" : "New task";
  box.querySelector("[data-cs-task-sub]")!.textContent = editing
    ? "Changes sync to the HubSpot task."
    : "Created in HubSpot and linked to the client. Shows up in To-do and on the client's Tasks tab.";
  box.querySelector<HTMLElement>("[data-cs-task-submit]")!.textContent = editing ? "Save changes" : "Create task";
  box.querySelector<HTMLElement>("[data-cs-task-delete]")!.hidden = !editing;
  const error = box.querySelector<HTMLElement>("[data-cs-task-error]");
  if (error) error.hidden = true;

  box.hidden = false;
  box.querySelector<HTMLInputElement>("input[name=title]")?.focus();
}

function closeTaskModal(): void {
  const box = modal();
  if (box) box.hidden = true;
}

function saved(message: string): void {
  closeTaskModal();
  showToast(message);
  document.dispatchEvent(new CustomEvent("cs:task-saved"));
}

// Exports what the Table shows: the current filters (URL) and the visible columns.
function exportCsv(): void {
  const url = new URL(window.location.href);
  if (document.querySelector<HTMLElement>("[data-cs-list]")?.dataset.view === "campaigns") {
    url.pathname = "/customer_success/campaigns.csv";
    window.location.assign(url.toString());
    return;
  }
  url.pathname = url.pathname.replace(/\/?$/, ".csv");
  url.searchParams.delete("columns[]");
  url.searchParams.delete("page");
  document.querySelectorAll<HTMLElement>("[data-cs-grid] thead th[data-col]:not([hidden])").forEach((th) => {
    if (th.dataset.col && th.dataset.col !== "actions") url.searchParams.append("columns[]", th.dataset.col);
  });
  window.location.assign(url.toString());
}

function showError(form: HTMLFormElement, message: string): void {
  const error = form.querySelector<HTMLElement>("[data-cs-task-error]");
  if (!error) return;
  error.textContent = message;
  error.hidden = false;
}

export function bindCsTasksExport(): void {
  document.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-cs-add-task]")) return openTaskModal();
    if (target.closest("[data-cs-modal-close]") || target.matches("[data-cs-task-modal]")) return closeTaskModal();
    if (target.closest("[data-cs-export]")) return exportCsv();

    const typeButton = target.closest<HTMLElement>("[data-cs-task-type-value]");
    const typeForm = typeButton?.closest("form");
    if (typeButton && typeForm) return setType(typeForm, typeButton.dataset.csTaskTypeValue || "TODO");

    const remove = target.closest<HTMLElement>("[data-cs-task-delete]");
    const removeForm = remove?.closest<HTMLFormElement>("form");
    if (remove && removeForm) {
      const id = removeForm.querySelector<HTMLInputElement>("[data-cs-task-id]")!.value;
      const body = new FormData();
      body.set("task[id]", id);
      const { ok } = await sendJson("/customer_success/archive_task", "DELETE", body, false);
      return ok ? saved("Task deleted") : showError(removeForm, "Couldn't delete the task");
    }
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modal() && !modal()!.hidden) closeTaskModal();
  });

  document.addEventListener("submit", async (e) => {
    const form = e.target as HTMLFormElement;
    if (!form.matches("[data-cs-task-form]")) return;
    e.preventDefault();

    const editing = Boolean(form.querySelector<HTMLInputElement>("[data-cs-task-id]")!.value);
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]");
    if (button) button.disabled = true;
    const url = editing ? "/customer_success/update_task" : "/customer_success/create_task";
    const { ok, data } = await sendJson(url, editing ? "PATCH" : "POST", new FormData(form), false);
    if (button) button.disabled = false;
    if (!ok) return showError(form, data.error || "Couldn't save the task");
    saved(editing ? "Task updated" : "Task added to To-do");
  });
}
